import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import CustomNoDataOverlay from "./CustomNoDataOverlay";
import NoDataComponent from "./NoDataComponent";

/**
 * CommonServerDataGrid Component
 * 
 * A reusable, server-side paginated DataGrid wrapper that handles:
 * - Multiple data format inputs (array, object, nested structure)
 * - Server-side pagination, filtering, and sorting
 * - Row selection (checkbox)
 * - Dynamic styling
 * 
 * @component
 * @example
 * <CommonServerDataGrid
 *   data={rows}
 *   columns={columns}
 *   getRowId={(row) => row.Id}
 *   role={userRole}
 *   pageSizeOptions={[25, 50, 100]}
 *   selectionModel={selectedRows}
 *   onSelectionModelChange={setSelectedRows}
 *   onPageChange={({ page, pageSize }) => fetchData(page, pageSize)}
 *   onFilterChange={setFilters}
 *   onSortChange={setSortModel}
 *   rowCount={totalCount}
 * />
 */
export default function CommonServerDataGrid({
  // Data input - flexible format support
  data,
  rows: rowsProp,

  // Grid configuration
  columns,
  getRowId = (r) => r.id ?? r.Id ?? r.key,
  role,
  pageSizeOptions = [5, 25, 50, 100],

  // Selection handling
  selectionModel: controlledSelection,
  onSelectionModelChange,

  // Server-side operations callbacks
  onPageChange,
  onFilterChange,
  onSortChange,

  // Row count for pagination
  rowCount,

  // Styling
  sx: sxProp,

  // Data mapping
  mapDataToRows,
  onFilterStatusChange,

  // Opt-in: skip the "return null when rows are momentarily empty" branch below.
  // Default (false) preserves existing behavior for all other callers.
  disableEmptyNull = false,

  // Pass through other props to DataGrid
  ...otherProps
}) {
  // ============ STATE MANAGEMENT ============

  /**
   * paginationModel: Controls which page and how many rows per page to display
   * - page: 0-indexed page number (0 = first page)
   * - pageSize: Number of rows per page
   */
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  /**
   * internalSelection: Tracks selected row IDs when component is uncontrolled
   * Used only if selectionModel prop is not provided by parent
   */
  const [internalSelection, setInternalSelection] = useState([]);

  /**
   * filterModel: Stores active filters applied by user
   * Structure: { items: [{ field: 'columnName', value: 'filterValue', operator: '...' }] }
   */
  const [filterModel, setFilterModel] = useState({ items: [] });

  /**
   * sortModel: Stores active sort applied by user
   * Structure: [{ field: 'columnName', sort: 'asc' | 'desc' }]
   */
  const [sortModel, setSortModel] = useState([]);

  // ============ DATA NORMALIZATION ============

  /**
   * normalizeInput: Converts various data formats into standardized { rows: [], total: number }
   * 
   * Supports multiple input formats:
   * 1. Array of objects: [{ id, name, ... }] → { rows: [...], total: length }
   * 2. Object with data property: { data: [...], total: 250 } → { rows: [...], total: 250 }
   * 3. Object with nested objects: { 1: {...}, 2: {...} } → { rows: [...], total: count }
   * 4. Custom mapping function: mapDataToRows(input) → processed data
   * 
   * @param {any} input - Raw data in any supported format
   * @returns {Object} Normalized data { rows: Array, total: Number }
   */
  const normalizeInput = (input) => {
    // ---- CASE 1: Custom mapper function provided ----
    // Parent component can provide custom transformation logic
    if (mapDataToRows) {
      const mapped = mapDataToRows(input);
      return {
        rows: Array.isArray(mapped) ? mapped : [],
        total: mapped?.length ?? 0
      };
    }

    // ---- CASE 2: No data provided ----
    if (!input) {
      return { rows: [], total: 0 };
    }

    // ---- CASE 3: Explicit rows prop (priority over data prop) ----
    // Use rowsProp if it's an array (direct rows array)
    if (Array.isArray(rowsProp)) {
      return {
        rows: rowsProp,
        total: rowsProp.length
      };
    }

    // ---- CASE 4: Data is a simple array ----
    // Most common case: data is already an array of objects
    // Example: data = [{ id: 1, name: 'Product 1' }, { id: 2, name: 'Product 2' }]
    if (Array.isArray(input)) {
      console.log(`[CommonServerDataGrid] Received array with ${input.length} items`);
      return {
        rows: input,
        total: input.length
      };
    }

    // ---- CASE 5: Data is an object with { data: [], total: N } structure ----
    // Common server response format
    // Example: { data: [...], total: 250, page: 1 }
    if (typeof input === "object" && Array.isArray(input.data)) {
      const total = Number(input.total ?? input.count ?? input.data.length ?? 0);
      console.log(`[CommonServerDataGrid] Received object.data with ${input.data.length} rows, total: ${total}`);
      return {
        rows: input.data,
        total: total
      };
    }

    // ---- CASE 6: Data is an object with nested objects (key-value pairs) ----
    // Example: { "1": { id: 1, name: 'Product 1' }, "2": { id: 2, name: 'Product 2' } }
    // This format sometimes comes from databases or APIs
    if (typeof input === "object") {
      const vals = Object.values(input).filter(
        (v) => v !== undefined && v !== null && typeof v === "object"
      );

      if (vals.length > 0) {
        console.log(`[CommonServerDataGrid] Received object with ${vals.length} nested objects`);
        return {
          rows: vals,
          total: vals.length
        };
      }
    }

    // ---- FALLBACK: Unknown format ----
    console.warn("[CommonServerDataGrid] Could not parse data format, returning empty", input);
    return {
      rows: [],
      total: 0
    };
  };

  // ============ EVENT HANDLERS ============

  /**
   * handleFilterChange: Called when user applies/modifies filters
   * - Resets to page 0 (because filtering changes row count)
   * - Notifies parent component about filter changes
   * 
   * @param {Object} model - Filter model from DataGrid
   */
  const handleFilterChange = (model) => {
    setFilterModel(model);
    setPaginationModel((p) => ({ ...p, page: 0 })); // Reset to first page
    onFilterChange?.(model); // Notify parent to fetch filtered data
  };

  /**
   * handleSortChange: Called when user clicks column header to sort
   * - Resets to page 0 (convention: sorted data shows from page 1)
   * - Notifies parent component about sort changes
   * 
   * @param {Array} model - Sort model from DataGrid (can have multiple sorts)
   */
  const handleSortChange = (model) => {
    setSortModel(model);
    setPaginationModel((p) => ({ ...p, page: 0 })); // Reset to first page
    onSortChange?.(model); // Notify parent to fetch sorted data
  };

  /**
   * handlePaginationModelChange: Called when user changes page or page size
   * - Updates local pagination state
   * - Notifies parent to fetch data for new page
   * 
   * @param {Object} model - Contains { page, pageSize }
   */
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    if (onPageChange) {
      onPageChange({ page: model.page, pageSize: model.pageSize });
    }
  };

  /**
   * handleSelectionChange: Called when user selects/deselects rows
   * - Converts IDs to proper type (number if possible, string otherwise)
   * - Updates controlled selection if provided by parent
   * - Updates internal selection if component is uncontrolled
   * 
   * @param {Array} newSel - Array of selected row IDs
   */
  const handleSelectionChange = (newSel) => {
    // Convert string IDs to numbers if they're numeric (for flexibility)
    const numeric = newSel.map((s) => {
      return isNaN(Number(s)) ? s : Number(s);
    });

    // Notify parent if callback provided
    if (onSelectionModelChange) {
      onSelectionModelChange(numeric);
    }

    // Update internal state only if not controlled by parent
    if (!controlledSelection) {
      setInternalSelection(numeric);
    }
  };

  // ============ DATA NORMALIZATION & MEMOIZATION ============

  /**
   * normalized: Memoized normalized data
   * - Runs normalizeInput() whenever data or mapDataToRows changes
   * - Overrides calculated total with rowCount if provided (for server pagination)
   * - Prevents unnecessary recalculations
   */
  const normalized = useMemo(() => {
    const result = normalizeInput(data ?? rowsProp);

    // If explicit rowCount provided (server-side pagination case)
    // Use it instead of calculating from rows length
    if (rowCount !== undefined && rowCount !== null) {
      console.log(`[CommonServerDataGrid] Using explicit rowCount: ${rowCount}`);
      result.total = rowCount;
    }

    return result;
  }, [data, rowsProp, mapDataToRows, rowCount]);

  // ============ INTERNAL STATE SYNCHRONIZATION ============

  /**
   * internalRows: Stores rows to display in grid
   * internalTotalCount: Stores total count for pagination calculations
   * 
   * These update whenever normalized data changes
   */
  const [internalRows, setInternalRows] = useState(normalized.rows);
  const [totalCount, setTotalCount] = useState(normalized.total);

  /**
   * useEffect: Synchronize internal state with normalized data
   * Runs whenever normalized data changes (data, rowCount, mapDataToRows changes)
   */
  useEffect(() => {
    setInternalRows(normalized.rows);
    setTotalCount(normalized.total);
    console.log(`[CommonServerDataGrid] Updated rows: ${normalized.rows.length}, total: ${normalized.total}`);
  }, [normalized.rows, normalized.total]);

  // ============ SELECTION LOGIC ============

  /**
   * selection: Determine which selection to use
   * - If parent provides controlled selection: use that
   * - Otherwise: use internal selection state (uncontrolled mode)
   * 
   * This allows both controlled and uncontrolled usage patterns
   */
  const selection = controlledSelection ?? internalSelection;

  // ============ STYLING ============

  /**
   * dataGridSx: MUI sx styling for the DataGrid component
   * 
   * Styling includes:
   * - Header: Gray background with bold text
   * - Rows: Alternating white/light gray backgrounds
   * - Hover: Light blue background
   * - Selected: Gray background
   * - Custom styling can be added via sx prop (spreads into ...sxProp)
   */
  const dataGridSx = {
    // Column header styling
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
    '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

    // Background styling
    '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

    // Row styling
    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
    '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

    // Selected row styling (with !important to override default MUI)
    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

    // Cell content alignment
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center'
    },

    // Merge custom styles from parent
    ...sxProp,
  };

  useEffect(() => {
  const active =
    filterModel?.items?.length > 0 &&
    filterModel.items.some(f => f.value && f.value.trim() !== "");

  onFilterStatusChange?.(active);
}, [filterModel]);


  // If there is no filter and rows are empty => return null (parent will show NoDataComponent)
  // Skipped when disableEmptyNull is set — unmounting/remounting the grid here
  // on every momentary empty-rows window (e.g. while a slow fetch is in flight)
  // causes a visible flash; callers that already own their own empty-state UI
  // can opt out and keep the grid mounted at all times.
if (
  !disableEmptyNull &&
  internalRows.length === 0 &&
  (filterModel?.items?.length === 0 || !filterModel.items.some(f => f.value))
) {
  return null;
}

  // ============ RENDER ============

  return (
    <DataGrid
      // Data
      rows={internalRows}
      columns={columns}
      getRowId={getRowId}
      getRowHeight={() => 'auto'}
      // Selection
      checkboxSelection={role !== "User"} // Hide checkboxes for Users
      rowSelectionModel={selection}
      onRowSelectionModelChange={handleSelectionChange}

      // Pagination (Server-side)
      pagination
      paginationMode="server" // Server handles pagination logic
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={pageSizeOptions}
      rowCount={totalCount} // Total count used to calculate page buttons

      // Sorting (Server-side)
      sortingMode="server" // Server handles sorting logic
      sortModel={sortModel}
      onSortModelChange={handleSortChange}

      // Filtering (Server-side)
      filterMode="server" // Server handles filtering logic
      filterModel={filterModel}
      onFilterModelChange={handleFilterChange}

      // Styling
      sx={dataGridSx}

      // Pass remaining props to DataGrid
      {...otherProps}
     slots={{
      noRowsOverlay: CustomNoDataOverlay,   // hide MUI default
      noResultsOverlay: CustomNoDataOverlay    // 🔥 Filter returns 0 rows
    }}
    />
  );
}

/**
 * ============ USAGE GUIDE ============
 * 
 * BASIC USAGE (with server data):
 * 
 * const [rows, setRows] = useState([]);
 * const [totalCount, setTotalCount] = useState(0);
 * const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
 * 
 * const fetchData = async ({ page, pageSize }) => {
 *   const response = await fetch(`/api/products?page=${page}&limit=${pageSize}`);
 *   const { data, total } = await response.json();
 *   setRows(data);
 *   setTotalCount(total);
 * };
 * 
 * useEffect(() => {
 *   fetchData(pagination);
 * }, [pagination]);
 * 
 * <CommonServerDataGrid
 *   data={rows}                           // Array of row objects
 *   columns={columns}                     // MUI DataGrid columns config
 *   getRowId={(row) => row.Id}           // Extract ID from each row
 *   role={userRole}                       // User role (hide checkboxes if 'User')
 *   rowCount={totalCount}                 // Total count for pagination
 *   pageSizeOptions={[25, 50, 100]}      // Page size options
 *   onPageChange={({ page, pageSize }) => {
 *     setPagination({ page, pageSize });
 *   }}
 *   onFilterChange={(filterModel) => {
 *     // Handle filter changes, notify server
 *   }}
 *   onSortChange={(sortModel) => {
 *     // Handle sort changes, notify server
 *   }}
 * />
 * 
 * ============ SUPPORTED DATA FORMATS ============
 * 
 * 1. Simple Array:
 *    data={[{ id: 1, name: 'Product 1' }, { id: 2, name: 'Product 2' }]}
 * 
 * 2. Object with data property:
 *    data={{ data: [...], total: 250 }}
 * 
 * 3. Object with count property:
 *    data={{ data: [...], count: 250 }}
 * 
 * 4. Nested object:
 *    data={{ "1": { id: 1, name: '...' }, "2": { id: 2, name: '...' } }}
 * 
 * 5. With rowCount prop (recommended for server pagination):
 *    data={rows} rowCount={totalCount}
 * 
 * ============ COMPONENT MODES ============
 * 
 * CONTROLLED MODE (parent manages selection):
 * <CommonServerDataGrid
 *   selectionModel={selectedIds}
 *   onSelectionModelChange={setSelectedIds}
 *   ...
 * />
 * 
 * UNCONTROLLED MODE (component manages selection):
 * <CommonServerDataGrid
 *   onSelectionModelChange={(ids) => console.log(ids)}
 *   ...
 * />
 */